import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import usePermission from '../../hooks/usePermission';
import {
  Truck, Plus, ArrowDownCircle, User, ChevronRight, X, Phone
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
  catch { return '—'; }
};
const todayStr = () => new Date().toISOString().split('T')[0];

function JobBadge({ status, expected_date }) {
  const isOverdue = status !== 'Received' && status !== 'Cancelled'
    && expected_date && new Date(expected_date) < new Date();

  const cfg = {
    Received: { bg: '#ECFDF5', text: '#065F46', label: '✓ Received' },
    Cancelled: { bg: '#F3F4F6', text: '#6B7280', label: '✗ Cancelled' },
    Partial: { bg: '#EFF6FF', text: '#1D4ED8', label: '≈ Partial' },
    Sent: { bg: isOverdue ? '#FEF2F2' : '#F5F3FF', text: isOverdue ? '#DC2626' : '#7C3AED', label: isOverdue ? '⚠ Overdue' : '→ Sent' },
  };
  const c = cfg[status] || cfg.Sent;
  return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.text }}>
      {c.label}
    </span>
  );
}

// ── Inline Quick Form ─────────────────────────────────────────────────────────
function InlineJobForm({ projectId, items, departments, onSaved, onCancel }) {
  const [form, setForm] = useState({
    project_item_id: '',
    department: '',
    vendor_name: '',
    vendor_phone: '',
    qty_sent: '',
    sent_date: todayStr(),
    expected_date: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/outsource-jobs', { ...form, project_id: projectId });
      toast.success('Outsource job added');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-amber-800 text-sm flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> New Outsource Job
        </h4>
        <button onClick={onCancel} className="text-amber-600 hover:text-amber-800">
          <X className="w-4 h-4" />
        </button>
      </div>
      <form onSubmit={handle} className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-amber-700 mb-1">Item</label>
          <select
            className="w-full border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            value={form.project_item_id}
            onChange={e => set('project_item_id', e.target.value)}
          >
            <option value="">-- Select Item --</option>
            {items.map(i => (
              <option key={i.id} value={i.id}>{i.item_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-amber-700 mb-1">Department</label>
          <select
            className="w-full border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            value={form.department}
            onChange={e => set('department', e.target.value)}
          >
            <option value="">-- Select Department --</option>
            {departments.map(d => (
              <option key={d.id} value={d.name}>{d.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-amber-700 mb-1">Vendor Name *</label>
          <input
            className="w-full border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            placeholder="e.g. Raju Metal Works"
            value={form.vendor_name}
            onChange={e => set('vendor_name', e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-amber-700 mb-1">Phone</label>
          <input
            className="w-full border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            placeholder="Optional"
            value={form.vendor_phone}
            onChange={e => set('vendor_phone', e.target.value)}
            type="tel"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-amber-700 mb-1">Qty Sent *</label>
          <input
            className="w-full border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            type="number" min="1"
            value={form.qty_sent}
            onChange={e => set('qty_sent', e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-amber-700 mb-1">Sent Date *</label>
          <input
            className="w-full border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            type="date"
            value={form.sent_date}
            onChange={e => set('sent_date', e.target.value)}
            required
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-semibold text-amber-700 mb-1">Expected Return Date *</label>
          <input
            className="w-full border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            type="date"
            value={form.expected_date}
            onChange={e => set('expected_date', e.target.value)}
            required
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-semibold text-amber-700 mb-1">Notes</label>
          <input
            className="w-full border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            placeholder="Optional..."
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
          />
        </div>
        <div className="col-span-2 flex gap-2">
          <button type="button" onClick={onCancel} className="flex-1 py-2 rounded-lg border border-amber-300 text-amber-700 text-xs font-semibold hover:bg-amber-100">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold disabled:opacity-60">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── OutsourceTab (main export) ────────────────────────────────────────────────
export default function OutsourceTab({ projectId, items = [] }) {
  const { canWrite, canEdit } = usePermission('outsource');
  const [jobs, setJobs]         = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [receivingId, setReceivingId] = useState(null);
  const [receiveQty, setReceiveQty]   = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, deptRes] = await Promise.all([
        api.get(`/outsource-jobs?project_id=${projectId}`),
        api.get('/departments?active_only=1'),
      ]);
      setJobs(jobsRes.data);
      setDepartments(deptRes.data || []);
    } catch {
      toast.error('Failed to load outsource data');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleQuickReceive = async (job) => {
    const pending = job.qty_sent - job.qty_received;
    if (pending <= 0) return;
    try {
      await api.patch(`/outsource-jobs/${job.id}/receive`, {
        qty_received: job.qty_received + receiveQty
      });
      toast.success('Updated');
      setReceivingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const pending = jobs.filter(j => !['Received','Cancelled'].includes(j.status));
  const overdue = pending.filter(j => j.expected_date && new Date(j.expected_date) < new Date());

  return (
    <div>
      {/* Mini summary */}
      {jobs.length > 0 && (
        <div className="flex gap-3 mb-4">
          {[
            { label: 'Total Jobs', value: jobs.length, color: '#7C3AED' },
            { label: 'Pending', value: pending.length, color: '#D97706' },
            { label: 'Overdue', value: overdue.length, color: '#DC2626' },
          ].map(c => (
            <div key={c.label} className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2 shadow-sm">
              <span className="text-lg font-black" style={{ color: c.color }}>{c.value}</span>
              <span className="text-xs text-gray-500">{c.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Add button */}
      {!showAdd && canWrite && (
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm rounded-xl mb-4 transition-colors"
        >
          <Plus className="w-4 h-4" /> New Outsource Job
        </button>
      )}

      {/* Inline form */}
      {showAdd && (
        <InlineJobForm
          projectId={projectId}
          items={items}
          departments={departments}
          onSaved={() => { setShowAdd(false); load(); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {/* Job list */}
      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Truck className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="font-semibold text-sm">No outsource jobs for this project</p>
          <p className="text-xs mt-1">Use the button above to add one</p>
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map(job => {
            const pending = job.qty_sent - job.qty_received;
            const pct = job.qty_sent > 0 ? Math.round((job.qty_received / job.qty_sent) * 100) : 0;
            const isActive = !['Received','Cancelled'].includes(job.status);
            const isRcv = receivingId === job.id;

            return (
              <div
                key={job.id}
                className={`bg-white border rounded-xl overflow-hidden ${
                  isActive && job.expected_date && new Date(job.expected_date) < new Date()
                    ? 'border-red-200 border-l-4 border-l-red-400'
                    : 'border-gray-100'
                }`}
              >
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                        <JobBadge status={job.status} expected_date={job.expected_date} />
                        {job.department && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {job.department}
                          </span>
                        )}
                        {job.item_name && (
                          <span className="text-xs text-gray-500">
                            <ChevronRight className="w-3 h-3 inline" />{job.item_name}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-sm">
                        <span className="font-semibold text-gray-800 flex items-center gap-1">
                          <User className="w-3 h-3 text-amber-500" />{job.vendor_name}
                        </span>
                        {job.vendor_phone && (
                          <a href={`tel:${job.vendor_phone}`} className="text-xs text-blue-500 flex items-center gap-0.5">
                            <Phone className="w-3 h-3" />{job.vendor_phone}
                          </a>
                        )}
                      </div>

                      {/* Progress bar */}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${pct}%`, background: pct === 100 ? '#10B981' : '#F59E0B' }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {job.qty_received}/{job.qty_sent} pcs
                        </span>
                      </div>

                      <div className="flex gap-3 mt-1.5 text-xs text-gray-400">
                        <span>Sent: {fmt(job.sent_date)}</span>
                        <span className={
                          isActive && job.expected_date && new Date(job.expected_date) < new Date()
                            ? 'text-red-600 font-semibold'
                            : ''
                        }>
                          Expected: {fmt(job.expected_date)}
                        </span>
                        {job.received_date && <span className="text-emerald-600">Got: {fmt(job.received_date)}</span>}
                      </div>
                    </div>

                    {/* Receive button */}
                    {isActive && pending > 0 && !isRcv && canEdit && (
                      <button
                        onClick={() => { setReceivingId(job.id); setReceiveQty(pending); }}
                        className="flex-shrink-0 px-2.5 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-bold rounded-lg transition-colors flex items-center gap-1"
                      >
                        <ArrowDownCircle className="w-3.5 h-3.5" />
                        Receive
                      </button>
                    )}
                  </div>

                  {/* Inline quick receive */}
                  {isRcv && (
                    <div className="mt-3 flex items-center gap-2 bg-emerald-50 rounded-lg p-2">
                      <span className="text-xs text-emerald-700 font-semibold whitespace-nowrap">How many received?</span>
                      <input
                        className="w-20 border border-emerald-300 rounded px-2 py-1 text-xs text-center focus:outline-none focus:ring-1 focus:ring-emerald-400"
                        type="number" min="1" max={pending}
                        value={receiveQty}
                        onChange={e => setReceiveQty(Math.min(parseInt(e.target.value)||0, pending))}
                      />
                      <span className="text-xs text-emerald-600">/ {pending} pending</span>
                      <button
                        onClick={() => handleQuickReceive(job)}
                        className="px-2.5 py-1 bg-emerald-500 text-white text-xs font-bold rounded hover:bg-emerald-600"
                      >
                        ✓ OK
                      </button>
                      <button
                        onClick={() => setReceivingId(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
